import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  GitBranchFromCurrentResponse,
  GitCommit,
  GitJumpResponse,
  GitPublishResponse,
  GitReturnResponse,
  GitSaveResponse,
  GitState,
  ProjectActivityItem,
  Session,
  Task,
} from '@claude-code-api/shared';
import { Activity, Loader2, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { HistoryPanel } from '@/components/talk/session/HistoryPanel';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useInterval } from '@/hooks/useInterval';
import { isTeamWorkflowRunner } from '@/lib/gift-agent';
import { useProjectTasksBoardStore } from '@/stores/projectTasksBoardStore';

type TeamAiPanel = 'chat' | 'threads' | 'versions';

type TeamChatThread = {
  id: string;
  name: string;
  title?: string;
  status: string;
  activeJobId: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
};

type TeamChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

type TeamChatJob = {
  id: string;
  status: string;
  error?: unknown;
};

type CodingActivityPulse = {
  taskId: string;
  rootTitle: string;
  subtaskTitle: string | null;
  message: string;
  status: string;
  phase: string;
  timestamp: string;
  elapsedLabel: string;
  active: boolean;
};

type PendingGitConfirm =
  | { kind: 'jump'; commit: GitCommit }
  | { kind: 'return' }
  | null;

interface ProjectTeamAiTabProps {
  projectId: string;
}

function sortSessions(sessions: Session[]) {
  return [...sessions].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
}

function getToastMethod(status: string) {
  if (status === 'error' || status === 'conflict_restore_failed')
    return toast.error;
  if (
    status === 'blocked_dirty_state' ||
    status === 'blocked_missing_upstream' ||
    status === 'travel_active'
  )
    return toast.warning;
  if (status === 'no_op') return toast.info;
  return toast.success;
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatElapsedTime(value: string) {
  const startedAt = new Date(value).getTime();
  if (!Number.isFinite(startedAt)) return '';
  const elapsedMs = Math.max(0, Date.now() - startedAt);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  if (totalSeconds < 60) return `${Math.max(1, totalSeconds)}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  if (totalHours <= 0) return `${minutes}m`;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
}

export function ProjectTeamAiTab({ projectId }: ProjectTeamAiTabProps) {
  const { t } = useTranslation(['projects']);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const [threads, setThreads] = useState<TeamChatThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TeamChatMessage[]>([]);
  const [activeJob, setActiveJob] = useState<TeamChatJob | null>(null);
  const [messageDraft, setMessageDraft] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<TeamAiPanel>('chat');
  const [threadSearch, setThreadSearch] = useState('');
  const [gitState, setGitState] = useState<GitState | null>(null);
  const [gitHistoryLoading, setGitHistoryLoading] = useState(false);
  const [gitHistoryError, setGitHistoryError] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(20);
  const [pendingGitConfirm, setPendingGitConfirm] =
    useState<PendingGitConfirm>(null);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [projectActivity, setProjectActivity] = useState<ProjectActivityItem[]>(
    [],
  );
  const setSelectedTaskId = useProjectTasksBoardStore(
    (state) => state.setSelectedTaskId,
  );
  const setPanelCollapsed = useProjectTasksBoardStore(
    (state) => state.setPanelCollapsed,
  );
  const setActiveView = useProjectTasksBoardStore(
    (state) => state.setActiveView,
  );
  const setDrawerTab = useProjectTasksBoardStore((state) => state.setDrawerTab);

  const fetchProjectSessions = useCallback(async () => {
    try {
      await fetch(`/api/projects/coding-session/health/${projectId}`, {
        credentials: 'include',
      }).catch(() => null);

      const response = await fetch(`/api/sessions?projectId=${projectId}`, {
        credentials: 'include',
      });
      if (!response.ok)
        throw new Error(
          t('projects:teamAi.errors.loadRuntimeWithStatus', {
            status: response.status,
          }),
        );
      const payload = await response.json();
      const runningCodeSessions = sortSessions(
        Array.isArray(payload?.data)
          ? payload.data.filter(
              (session: Session) =>
                session.status === 'running' &&
                isTeamWorkflowRunner(session.runnerType),
            )
          : [],
      );
      setSessions(runningCodeSessions);
      setSelectedSessionId((current) => {
        if (
          current &&
          runningCodeSessions.some((session) => session.id === current)
        )
          return current;
        return runningCodeSessions[0]?.id || null;
      });
    } catch (error) {
      console.error('Failed to load coding runtime:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:teamAi.errors.loadRuntime'),
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    setSessions([]);
    setSelectedSessionId(null);
    setGitState(null);
    setGitHistoryError(null);
    setGitHistoryLoading(false);
    setSessionsLoading(true);
  }, [projectId]);

  const fetchThreads = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setThreadsLoading(true);
      try {
        const response = await fetch(
          `/api/projects/team-chat/threads/${projectId}`,
          { credentials: 'include' },
        );
        if (!response.ok)
          throw new Error(
            t('projects:teamAi.errors.loadThreadsWithStatus', {
              status: response.status,
            }),
          );
        const payload = await response.json();
        const nextThreads = Array.isArray(payload?.threads)
          ? (payload.threads as TeamChatThread[])
          : [];
        setThreads(nextThreads);
        setActiveThreadId((current) => {
          if (current && nextThreads.some((thread) => thread.id === current))
            return current;
          return nextThreads[0]?.id || null;
        });
      } catch (error) {
        console.error('Failed to load PM threads:', error);
        toast.error(
          error instanceof Error
            ? error.message
            : t('projects:teamAi.errors.loadThreads'),
        );
      } finally {
        setThreadsLoading(false);
      }
    },
    [projectId, t],
  );

  const fetchMessages = useCallback(
    async (threadId: string, options?: { silent?: boolean }) => {
      if (!threadId) return;
      try {
        const response = await fetch(
          `/api/projects/team-chat/threads/messages/${projectId}/${threadId}`,
          { credentials: 'include' },
        );
        if (!response.ok)
          throw new Error(
            t('projects:teamAi.errors.loadMessagesWithStatus', {
              status: response.status,
            }),
          );
        const payload = await response.json();
        setMessages(
          Array.isArray(payload?.messages)
            ? (payload.messages as TeamChatMessage[])
            : [],
        );
        setActiveJob((payload?.activeJob as TeamChatJob | null) || null);
      } catch (error) {
        if (!options?.silent) {
          console.error('Failed to load PM messages:', error);
          toast.error(
            error instanceof Error
              ? error.message
              : t('projects:teamAi.errors.loadMessages'),
          );
        }
      }
    },
    [projectId, t],
  );

  const fetchGitHistory = useCallback(
    async (sessionId: string, options?: { silent?: boolean }) => {
      if (!sessionId) return;
      if (!options?.silent) setGitHistoryLoading(true);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${sessionId}/git-state`,
          { credentials: 'include' },
        );
        if (!response.ok)
          throw new Error(
            t('projects:teamAi.errors.loadGitHistoryWithStatus', {
              status: response.status,
            }),
          );
        const payload = await response.json();
        setGitState((payload || null) as GitState | null);
        setGitHistoryError(null);
      } catch (error) {
        console.error('Failed to load git history:', error);
        setGitHistoryError(
          error instanceof Error
            ? error.message
            : t('projects:teamAi.errors.loadGitHistory'),
        );
      } finally {
        setGitHistoryLoading(false);
      }
    },
    [t],
  );

  const fetchCodingActivity = useCallback(async () => {
    try {
      const [tasksResponse, activityResponse] = await Promise.all([
        fetch(`/api/projects/tasks/${projectId}?limit=200`, {
          credentials: 'include',
        }),
        fetch(`/api/projects/activity/${projectId}`, {
          credentials: 'include',
        }),
      ]);

      if (tasksResponse.ok) {
        const payload = await tasksResponse.json().catch(() => ({}));
        setProjectTasks(Array.isArray(payload?.tasks) ? payload.tasks : []);
      }
      if (activityResponse.ok) {
        const payload = await activityResponse.json().catch(() => ({}));
        setProjectActivity(Array.isArray(payload?.data) ? payload.data : []);
      }
    } catch (error) {
      console.error('Failed to load coding activity:', error);
    }
  }, [projectId]);

  useEffect(() => {
    void fetchProjectSessions();
    void fetchThreads();
    void fetchCodingActivity();
  }, [fetchCodingActivity, fetchProjectSessions, fetchThreads]);

  useEffect(() => {
    if (activeThreadId) void fetchMessages(activeThreadId);
  }, [activeThreadId, fetchMessages]);

  useEffect(() => {
    if (
      selectedSessionId &&
      sessions.some((session) => session.id === selectedSessionId)
    ) {
      void fetchGitHistory(selectedSessionId);
    }
  }, [fetchGitHistory, selectedSessionId, sessions]);

  useInterval(() => {
    void fetchThreads({ silent: true });
    if (activeThreadId) void fetchMessages(activeThreadId, { silent: true });
    if (
      selectedSessionId &&
      sessions.some((session) => session.id === selectedSessionId)
    ) {
      void fetchGitHistory(selectedSessionId, { silent: true });
    }
    void fetchCodingActivity();
  }, 5000);

  const createThread = useCallback(async () => {
    setActionLoading('create');
    try {
      const response = await fetch(
        `/api/projects/team-chat/threads/${projectId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: t('projects:teamAi.threads.newThread'),
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(String(payload?.error || `HTTP ${response.status}`));
      await fetchThreads({ silent: true });
      setActiveThreadId(String(payload?.thread?.id || ''));
      setMessages([]);
      setActivePanel('chat');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:teamAi.errors.createThread'),
      );
    } finally {
      setActionLoading(null);
    }
  }, [fetchThreads, projectId, t]);

  const createThreadForMessage = useCallback(
    async (content: string) => {
      const response = await fetch(
        `/api/projects/team-chat/threads/${projectId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title:
              content.trim().slice(0, 80) ||
              t('projects:teamAi.threads.newThread'),
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(String(payload?.error || `HTTP ${response.status}`));
      const threadId = String(payload?.thread?.id || '').trim();
      if (!threadId)
        throw new Error(t('projects:teamAi.errors.threadNotCreated'));
      setActiveThreadId(threadId);
      setActivePanel('chat');
      return threadId;
    },
    [projectId, t],
  );

  const sendMessage = useCallback(async () => {
    const content = messageDraft.trim();
    if (!content) return;
    setActionLoading('send');
    setMessageDraft('');
    try {
      const threadId =
        activeThreadId || (await createThreadForMessage(content));
      const response = await fetch(
        `/api/projects/team-chat/threads/messages/${projectId}/${threadId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ content }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(String(payload?.error || `HTTP ${response.status}`));
      await Promise.all([
        fetchMessages(threadId, { silent: true }),
        fetchThreads({ silent: true }),
      ]);
    } catch (error) {
      setMessageDraft(content);
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:teamAi.errors.sendMessage'),
      );
    } finally {
      setActionLoading(null);
    }
  }, [
    activeThreadId,
    createThreadForMessage,
    fetchMessages,
    fetchThreads,
    messageDraft,
    projectId,
    t,
  ]);

  const runGitCommand = useCallback(
    async <
      T extends
        | GitSaveResponse
        | GitPublishResponse
        | GitJumpResponse
        | GitReturnResponse
        | GitBranchFromCurrentResponse,
    >(
      endpoint: string,
      loadingKey: string,
      body?: Record<string, unknown>,
    ) => {
      if (!selectedSessionId) return null;
      setActionLoading(loadingKey);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${selectedSessionId}${endpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body || {}),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(String(data?.error || `HTTP ${response.status}`));
        const result = data as T;
        if (result?.state) setGitState(result.state);
        const notify = getToastMethod(String(result?.status || 'ok'));
        notify(
          String(
            result?.message || t('projects:teamAi.git.operationCompleted'),
          ),
          {
            description:
              result?.commitMessage ||
              result?.commitHash ||
              result?.recoveryBranch ||
              undefined,
          },
        );
        return result;
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t('projects:teamAi.errors.gitOperation'),
        );
        return null;
      } finally {
        setActionLoading(null);
      }
    },
    [selectedSessionId, t],
  );

  const filteredThreads = useMemo(() => {
    const keyword = threadSearch.trim().toLowerCase();
    if (!keyword) return threads;
    return threads.filter(
      (thread) =>
        thread.name.toLowerCase().includes(keyword) ||
        thread.id.toLowerCase().includes(keyword),
    );
  }, [threadSearch, threads]);

  const filteredHistoryCommits = useMemo(() => {
    const commits = gitState?.commits || [];
    const keyword = historySearch.trim().toLowerCase();
    if (!keyword) return commits;
    return commits.filter(
      (commit) =>
        commit.subject.toLowerCase().includes(keyword) ||
        commit.author.toLowerCase().includes(keyword) ||
        commit.shortHash.toLowerCase().includes(keyword) ||
        commit.hash.toLowerCase().includes(keyword),
    );
  }, [gitState?.commits, historySearch]);

  const visibleHistoryCommits = useMemo(
    () => filteredHistoryCommits.slice(0, visibleHistoryCount),
    [filteredHistoryCommits, visibleHistoryCount],
  );

  const codingPulse = useMemo<CodingActivityPulse | null>(() => {
    if (projectTasks.length === 0) return null;
    const taskById = new Map(projectTasks.map((task) => [task.id, task]));
    const activeTask =
      projectTasks.find((task) =>
        ['in_progress', 'assigned'].includes(String(task.status)),
      ) || null;
    const activityTask =
      projectActivity
        .map((activity) => taskById.get(activity.taskId))
        .find(Boolean) || null;
    const task = activeTask || activityTask;
    if (!task) return null;

    const rootTask = taskById.get(task.rootTaskId || task.id) || task;
    const recentActivity =
      projectActivity.find((activity) => activity.taskId === task.id) ||
      projectActivity.find((activity) => activity.taskId === rootTask.id) ||
      projectActivity[0] ||
      null;
    const active = ['in_progress', 'assigned'].includes(String(task.status));
    const elapsedFrom = String(
      task.startedAt ||
        task.heartbeatAt ||
        recentActivity?.createdAt ||
        task.updatedAt ||
        '',
    );

    return {
      taskId: task.id,
      rootTitle:
        rootTask.title ||
        rootTask.prompt ||
        t('projects:teamAi.activity.untitledTask'),
      subtaskTitle:
        rootTask.id !== task.id
          ? task.title ||
            task.prompt ||
            t('projects:teamAi.activity.untitledSubtask')
          : null,
      message:
        recentActivity?.message ||
        (active
          ? t('projects:teamAi.activity.codingWorking')
          : t('projects:teamAi.activity.latest')),
      status: String(task.status || ''),
      phase: String(task.phase || ''),
      timestamp: recentActivity?.createdAt
        ? String(recentActivity.createdAt)
        : String(task.updatedAt || ''),
      elapsedLabel: formatElapsedTime(elapsedFrom),
      active,
    };
  }, [projectActivity, projectTasks, t]);

  const openCodingPulseHistory = useCallback(() => {
    if (!codingPulse) return;
    setSelectedTaskId(projectId, codingPulse.taskId);
    setPanelCollapsed(projectId, false);
    setActiveView(projectId, 'board');
    setDrawerTab(projectId, 'activity');
  }, [
    codingPulse,
    projectId,
    setActiveView,
    setDrawerTab,
    setPanelCollapsed,
    setSelectedTaskId,
  ]);

  const requiresGitConfirm = !!gitState?.dirty;

  const handleJumpToCommit = useCallback(
    (commit: GitCommit) => {
      if (requiresGitConfirm) {
        setPendingGitConfirm({ kind: 'jump', commit });
        return;
      }
      void runGitCommand('/git/jump', `jump:${commit.hash}`, {
        targetCommitHash: commit.hash,
      });
    },
    [requiresGitConfirm, runGitCommand],
  );

  const handleJumpBack = useCallback(() => {
    if (requiresGitConfirm) {
      setPendingGitConfirm({ kind: 'return' });
      return;
    }
    void runGitCommand('/git/return', 'return');
  }, [requiresGitConfirm, runGitCommand]);

  if (sessionsLoading || threadsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-14 rounded-xl" />
        <Skeleton className="h-[68dvh] rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-border/70 bg-background shadow-sm">
      <div className="flex items-center gap-3 border-b border-border/70 px-5 py-3">
        <Tabs
          value={activePanel}
          onValueChange={(value) => setActivePanel(value as TeamAiPanel)}
        >
          <TabsList className="h-auto rounded-lg bg-muted/60 p-1">
            <TabsTrigger value="chat" className="min-w-[5.5rem]">
              {t('projects:teamAi.tabs.chat')}
            </TabsTrigger>
            <TabsTrigger value="threads" className="min-w-[5.5rem]">
              {t('projects:teamAi.tabs.threads')}
            </TabsTrigger>
            <TabsTrigger value="versions" className="min-w-[5.5rem]">
              {t('projects:teamAi.tabs.versions')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="ml-auto flex items-center gap-2">
          {activePanel === 'chat' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void createThread()}
              disabled={actionLoading === 'create'}
            >
              {actionLoading === 'create' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('projects:teamAi.actions.newThread')}
            </Button>
          ) : null}
          {activePanel === 'versions' ? (
            <Button
              type="button"
              size="sm"
              onClick={() => void runGitCommand('/git/publish', 'publish')}
              disabled={!selectedSessionId || !!actionLoading}
            >
              {actionLoading === 'publish' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('projects:teamAi.actions.save')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1">
        {activePanel === 'chat' ? (
          <div className="flex h-full min-h-0 flex-col">
            {codingPulse ? (
              <button
                type="button"
                onClick={openCodingPulseHistory}
                className="sticky top-0 z-10 flex h-9 w-full min-w-0 items-center gap-2 border-b border-border/70 bg-background/95 px-4 text-left text-xs backdrop-blur transition-colors hover:bg-muted/50"
                title={`${codingPulse.rootTitle}${codingPulse.subtaskTitle ? ` / ${codingPulse.subtaskTitle}` : ''}: ${codingPulse.message}`}
              >
                {codingPulse.active ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-amber-500" />
                ) : (
                  <Activity className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="shrink-0 font-medium text-muted-foreground">
                  {t('projects:teamAi.labels.codingAi')}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-foreground">
                    {codingPulse.rootTitle}
                  </span>
                  {codingPulse.subtaskTitle ? (
                    <>
                      <span className="px-1 text-muted-foreground">/</span>
                      <span>{codingPulse.subtaskTitle}</span>
                    </>
                  ) : null}
                  <span className="px-1.5 text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    {codingPulse.message}
                  </span>
                </span>
                <span className="shrink-0 text-[10px] tracking-wide text-muted-foreground">
                  {[
                    codingPulse.elapsedLabel,
                    codingPulse.phase || codingPulse.status,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            ) : null}
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {!activeThreadId ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t('projects:teamAi.chat.emptyNoThread')}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t('projects:teamAi.chat.emptyThread')}
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`min-w-0 max-w-[86%] overflow-hidden rounded-lg px-3 py-2 text-sm ${
                      message.role === 'user'
                        ? 'ml-auto bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words leading-6 [overflow-wrap:anywhere]">
                      {message.content}
                    </div>
                    <div className="mt-1 text-[10px] opacity-60">
                      {message.role === 'user'
                        ? t('projects:teamAi.labels.you')
                        : t('projects:teamAi.labels.pmAi')}{' '}
                      · {formatTime(message.createdAt)}
                    </div>
                  </div>
                ))
              )}
              {activeJob && ['queued', 'running'].includes(activeJob.status) ? (
                <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('projects:teamAi.chat.workerStatus', {
                    status: activeJob.status,
                  })}
                </div>
              ) : null}
            </div>
            <div className="border-t border-border/70 p-3">
              <div className="flex gap-2">
                <textarea
                  rows={1}
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void sendMessage();
                    }
                  }}
                  placeholder={t('projects:teamAi.chat.placeholder')}
                  className="h-11 min-h-11 flex-1 resize-none overflow-hidden rounded-lg border bg-background px-3 py-2.5 text-sm leading-5 outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={
                    actionLoading === 'send' ||
                    Boolean(
                      activeJob &&
                        ['queued', 'running'].includes(activeJob.status),
                    )
                  }
                />
                <Button
                  type="button"
                  className="h-11 w-11 shrink-0 p-0"
                  onClick={() => void sendMessage()}
                  disabled={
                    !messageDraft.trim() ||
                    actionLoading === 'send' ||
                    Boolean(
                      activeJob &&
                        ['queued', 'running'].includes(activeJob.status),
                    )
                  }
                >
                  {actionLoading === 'send' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : activePanel === 'threads' ? (
          <div className="h-full min-h-0 overflow-y-auto p-4">
            <div className="sticky top-0 z-10 border-b bg-background/95 pb-3 backdrop-blur">
              <Input
                value={threadSearch}
                onChange={(event) => setThreadSearch(event.target.value)}
                placeholder={t('projects:teamAi.threads.searchPlaceholder')}
                className="h-10 rounded-full"
              />
            </div>
            <div className="mt-3 space-y-2">
              {filteredThreads.length === 0 ? (
                <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
                  {t('projects:teamAi.threads.empty')}
                </div>
              ) : (
                filteredThreads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => {
                      setActiveThreadId(thread.id);
                      setActivePanel('chat');
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
                      thread.id === activeThreadId
                        ? 'border-primary/50 bg-primary/5'
                        : 'hover:border-primary/30'
                    }`}
                  >
                    <div className="truncate text-sm font-medium">
                      {thread.name ||
                        thread.title ||
                        t('projects:teamAi.threads.fallbackTitle')}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('projects:teamAi.threads.messageCount', {
                        count: thread.messageCount,
                      })}{' '}
                      · {formatTime(thread.updatedAt)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {!selectedSessionId && sessions.length === 0 ? (
                <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
                  {t('projects:teamAi.versions.runtimeMissing')}
                </div>
              ) : (
                <HistoryPanel
                  loading={gitHistoryLoading}
                  error={gitHistoryError}
                  history={gitState}
                  historySearch={historySearch}
                  visibleCommits={visibleHistoryCommits}
                  filteredCommitCount={filteredHistoryCommits.length}
                  actionLoading={actionLoading}
                  onHistorySearchChange={setHistorySearch}
                  onLoadMore={() =>
                    setVisibleHistoryCount((count) => count + 20)
                  }
                  onJumpTo={handleJumpToCommit}
                  onJumpBack={handleJumpBack}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingGitConfirm}
        onOpenChange={(open) => {
          if (!open && !actionLoading) setPendingGitConfirm(null);
        }}
        title={
          pendingGitConfirm?.kind === 'return'
            ? t('projects:teamAi.confirm.returnTitle')
            : t('projects:teamAi.confirm.jumpTitle')
        }
        description={
          pendingGitConfirm?.kind === 'return'
            ? t('projects:teamAi.confirm.returnDescription')
            : t('projects:teamAi.confirm.jumpDescription')
        }
        confirmLabel={t('projects:teamAi.confirm.continue')}
        variant="default"
        loading={
          actionLoading === 'return' || actionLoading?.startsWith('jump:')
        }
        onConfirm={() => {
          if (pendingGitConfirm?.kind === 'jump') {
            void runGitCommand(
              '/git/jump',
              `jump:${pendingGitConfirm.commit.hash}`,
              { targetCommitHash: pendingGitConfirm.commit.hash },
            ).finally(() => setPendingGitConfirm(null));
            return;
          }
          if (pendingGitConfirm?.kind === 'return') {
            void runGitCommand('/git/return', 'return').finally(() =>
              setPendingGitConfirm(null),
            );
          }
        }}
      />
    </div>
  );
}
